import { AxiosError } from 'axios';
import { apiClient } from './api/client';

export interface TelemetryHistoryParams {
  type: string;
  registryId: string;
  deviceId: string;
  startTime: number | string;
  endTime: number | string;
}

export interface LastTelemetryDTO {
  timestamp: number | string;
  temperature?: number | string;
  humidity?: number | string;
  pressure?: number | string;
  co2?: number | string;
  tvoc?: number | string;
  radar?: number | string | import('@/types/radar').RadarTarget[];
  rssi?: number | string;
  lqi?: number | string;
  air_quality?: number | string;
  [key: string]: any;
}

export interface DeviceInfoDTO {
  response?: {
    lastTelemetry?: LastTelemetryDTO;
    deviceAppName?: string;
    fwVersion?: string;
    hwVersion?: string;
    otNetStatus?: string;
    joiningTime?: number | string;
  };
  lastTelemetry?: LastTelemetryDTO; // some endpoints return this shape
  [key: string]: any;
}

export interface TelemetryHistoryDTO {
  timestamps: number[];
  telemetry: Record<string, number[]>; // keyed by measurement
}

export interface DeviceSettingsDTO {
  [key: string]: any;
}

export interface AlarmEntryDTO {
  type: string;
  threshold_ON?: number;
  excess_value_ON?: number;
  threshold_OFF?: number;
  excess_value_OFF?: number;
  active?: boolean;
  timestamp_ON?: number | string;
  timestamp_OFF?: number | string;
  [key: string]: any;
}

export class DeviceService {
  static async getDeviceInfo(registryId: string, deviceId: string): Promise<DeviceInfoDTO> {
    try {
      const response = await apiClient.axios.post(`/sensor_devices/get_device_info`, { registryId, deviceId });
      return response.data as DeviceInfoDTO;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`getDeviceInfo failed: ${e.message}`);
    }
  }

  static async getTelemetryHistory(params: TelemetryHistoryParams, signal?: AbortSignal): Promise<{ timestamps: number[]; series: any[] }> {
    try {
      const response = await apiClient.axios.get(`/sensor_devices/telemetry_history`, {
        params,
        signal,
      });
      const data = response.data || {};
      // Support multiple backend shapes
      // Shape A: { timestamps: number[], telemetry: number[] }
      // Shape B: { telemetry: Array<{ timestamp: number, value: number }> }
      // Shape C: { telemetryTimestamps: number[], [type]: number[] }
      let timestamps: number[] = [];
      let series: any[] = [];
      const isRadar = params.type === 'radar';

      if (Array.isArray(data.telemetry) && data.telemetry.length > 0 && typeof data.telemetry[0] === 'object') {
        // Shape B: array of { timestamp, value } objects
        // For radar, value may be an array of targets — preserve as-is
        if (isRadar && 'distance' in data.telemetry[0]) {
          // Edge case: flat array of target objects (not wrapped per-timestamp)
          // Treat as single reading
          timestamps = [Number(data.telemetry[0].timestamp || 0)];
          series = [data.telemetry];
        } else {
          timestamps = data.telemetry.map((s: any) => Number(s.timestamp));
          series = data.telemetry.map((s: any) => isRadar ? s.value : Number(s.value));
        }
      } else {
        // Shape A/C
        timestamps = Array.isArray(data.timestamps) ? data.timestamps : (data.telemetryTimestamps || []);
        if (Array.isArray(data.telemetry)) {
          series = data.telemetry;
        } else if (Array.isArray((data as any)[params.type])) {
          series = (data as any)[params.type];
        } else {
          series = [];
        }
      }
      return { timestamps, series };
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`getTelemetryHistory failed: ${e.message}`);
    }
  }

  static async getDeviceSettings(registryId: string, deviceId: string): Promise<DeviceSettingsDTO> {
    try {
      const response = await apiClient.axios.post(`/sensor_devices/get_device_settings`, { registryId, deviceId });
      return response.data as DeviceSettingsDTO;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`getDeviceSettings failed: ${e.message}`);
    }
  }

  static async getJoinState(registryId: string, deviceId: string): Promise<any> {
    try {
      const response = await apiClient.axios.post(`/sensor_devices/get_join_state`, { registryId, deviceId });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`getJoinState failed: ${e.message}`);
    }
  }

  static async getGatewayInfo(registryId: string): Promise<any> {
    try {
      const response = await apiClient.axios.post(`/gateways/get_gateway_info`, { registryId });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`getGatewayInfo failed: ${e.message}`);
    }
  }

  static async forceRemoveDevice(registryId: string, deviceId: string): Promise<any> {
    try {
      const gatewayId = registryId.startsWith('CGReg_') ? registryId.split('CGReg_')[1] : registryId;
      const response = await apiClient.axios.post(`/sensor_devices/force_delete_device`, { gatewayId, deviceId });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`forceRemoveDevice failed: ${e.message}`);
    }
  }

  static async getAlarmsHistory(registryId: string, deviceId: string, startTime: number | string, endTime: number | string): Promise<AlarmEntryDTO[]> {
    try {
      const response = await apiClient.axios.get(`/sensor_devices/alarms_history`, {
        params: { registryId, deviceId, startTime, endTime },
      });
      return (response.data?.alarms || []) as AlarmEntryDTO[];
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`getAlarmsHistory failed: ${e.message}`);
    }
  }

  static async alarmsHistoryPerUser(userUid: string, startTime: number | string, endTime: number | string): Promise<any> {
    try {
      const response = await apiClient.axios.get(`/sensor_devices/alarms_history_per_user`, {
        params: { userUid, startTime, endTime },
      });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`alarmsHistoryPerUser failed: ${e.message}`);
    }
  }

  static async alarmSeen(registryId: string, deviceId: string, alarmId: string, alarmType: string): Promise<any> {
    try {
      const response = await apiClient.axios.post(`/sensor_devices/alarm_seen`, { registryId, deviceId, alarmId, alarmType });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`alarmSeen failed: ${e.message}`);
    }
  }

  static async alarmDismissed(registryId: string, deviceId: string, alarmId: string, alarmType: string): Promise<any> {
    try {
      const response = await apiClient.axios.post(`/sensor_devices/alarm_dismissed`, { registryId, deviceId, alarmId, alarmType });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`alarmDismissed failed: ${e.message}`);
    }
  }

  static async readBorderRouterData(gatewayId: string): Promise<any> {
    try {
      const response = await apiClient.axios.post(`/database/read_border_router_data`, { gatewayId });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`readBorderRouterData failed: ${e.message}`);
    }
  }

  static async triggerRadarCalibration(registryId: string, deviceId: string): Promise<{ message: string }> {
    try {
      const response = await apiClient.axios.post('/sensor_devices/trigger_radar_calibration', {
        registryId,
        deviceId,
      });
      return response.data as { message: string };
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`triggerRadarCalibration failed: ${e.message}`);
    }
  }

  static async readRoomSensorData(deviceId: string): Promise<any> {
    try {
      const response = await apiClient.axios.post(`/database/read_room_sensor_data`, { deviceId });
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`readRoomSensorData failed: ${e.message}`);
    }
  }

  static async setDeviceSettings(registryId: string, deviceId: string, settings: any): Promise<any> {
    try {
      // Extract gatewayId from registryId (format: CGReg_{gatewayId})
      const gatewayId = registryId.startsWith('CGReg_') ? registryId.split('CGReg_')[1] : registryId;

      // Backend expects 'data' as a JSON string
      const body = {
        gatewayId,
        deviceId,
        data: JSON.stringify(settings)
      };

      const response = await apiClient.axios.post(`/sensor_devices/change_sensor_device_settings`, body);
      return response.data;
    } catch (error) {
      const e = error as AxiosError;
      throw new Error(`setDeviceSettings failed: ${e.message}`);
    }
  }
}


